import { YoklaAssignment, ScannedData, QuestionData } from '../types';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import app from '../firebase';

// A4 ölçüləri (piksel)
const A4_WIDTH = 2232;
const A4_HEIGHT = 2232;
const MARGIN = 50; // Kenar boşluğu
const HEADER_HEIGHT = 120; // Header yüksəkliyi
const CONTENT_WIDTH = A4_WIDTH - (MARGIN * 2);
const MAX_CONTENT_HEIGHT = A4_HEIGHT - (MARGIN * 2) - HEADER_HEIGHT;

// Şagird adını mask et
const maskName = (firstName: string, lastName: string): string => {
  const maskedFirst = firstName.charAt(0) + '*'.repeat(Math.max(1, firstName.length - 1));
  const maskedLast = '*'.repeat(Math.max(1, lastName.length));
  return `${maskedFirst} ${maskedLast}`;
};

// Şagird ID-ni mask et
const maskStudentId = (studentId: string): string => {
  if (studentId.length <= 2) return '*'.repeat(studentId.length);
  return studentId.charAt(0) + '*'.repeat(studentId.length - 1);
};

// Firebase Storage URL-dən path çıxar
const extractPathFromFirebaseUrl = (url: string): string | null => {
  try {
    // URL format: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media&token=TOKEN
    const match = url.match(/\/o\/([^?]+)/);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    return null;
  } catch (error) {
    console.error('❌ URL-dən path çıxarıla bilmədi:', error);
    return null;
  }
};

// Firebase Storage-dan şəkil yüklə (CORS problemi üçün SDK istifadə et)
const loadImageFromFirebase = async (url: string): Promise<string> => {
  try {
    // Firebase Storage path-i çıxar
    const path = extractPathFromFirebaseUrl(url);
    
    if (path) {
      // Firebase Storage SDK istifadə et - getDownloadURL ilə CORS-friendly URL al
      const storage = getStorage(app);
      const storageRef = ref(storage, path);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Download URL-dən fetch et (Firebase SDK URL-ləri CORS-friendly olmalıdır)
      const response = await fetch(downloadURL);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Base64-ə çevir
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } else {
      // Path çıxarıla bilmədi, birbaşa URL istifadə et
      throw new Error('Firebase Storage path çıxarıla bilmədi');
    }
  } catch (error) {
    console.error('❌ Firebase Storage SDK ilə şəkil yüklənə bilmədi:', error);
    // Fallback: Orijinal URL-dən birbaşa yüklə (CORS problemi ola bilər)
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (fallbackError) {
      throw new Error(`Şəkil yüklənə bilmədi: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
};

// Şəkil yüklə və base64 formatında data URL yarat (CORS problemi üçün)
const loadImageAsDataUrl = async (url: string): Promise<string> => {
  // Firebase Storage URL-i olub-olmadığını yoxla
  if (url.includes('firebasestorage.googleapis.com')) {
    return await loadImageFromFirebase(url);
  }
  
  // Digər URL-lər üçün normal fetch
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('❌ Şəkil base64-ə çevrilə bilmədi:', error);
    throw error;
  }
};

// Şəkil yüklə və Image obyektinə çevir (ölçüləri almaq üçün)
// CORS problemi üçün: Firebase Storage SDK-dan getDownloadURL istifadə et
const loadImage = async (url: string): Promise<HTMLImageElement> => {
  // Firebase Storage URL-i olub-olmadığını yoxla
  if (url.includes('firebasestorage.googleapis.com')) {
    try {
      const path = extractPathFromFirebaseUrl(url);
      if (path) {
        // Firebase Storage SDK istifadə et
        const storage = getStorage(app);
        const storageRef = ref(storage, path);
        const downloadURL = await getDownloadURL(storageRef);
        
        // Download URL-dən Image yüklə
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = downloadURL;
        });
      }
    } catch (error) {
      console.warn('⚠️ Firebase Storage SDK ilə yükləmə uğursuz oldu, fallback istifadə edilir:', error);
    }
  }
  
  // Fallback: Base64 data URL yarat
  const dataUrl = await loadImageAsDataUrl(url);
  
  // Image obyektinə yüklə
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
};

// Şəkil ölçülərini hesabla (A4 genişliyinə uyğun)
const calculateImageDimensions = (
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } => {
  const aspectRatio = img.width / img.height;
  let width = maxWidth;
  let height = width / aspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return { width, height };
};

// PDF export funksiyası
export const exportBuraxilisQuestionPDF = async (
  assignment: YoklaAssignment,
  questionNumber: string,
  students: ScannedData[],
  onProgress?: (current: number, total: number) => void
): Promise<void> => {
  // jsPDF dinamik import
  const { default: jsPDF } = await import('jspdf');

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [A4_WIDTH, A4_HEIGHT]
  });

  const questionNumberDisplay = questionNumber.replace(/^Q/i, '');
  
  // Şagirdləri document ID-yə görə sırala (BuraxilisYoxlamaPage-dəki kimi)
  const sortedStudents = [...students].sort((a, b) => {
    const idA = a.id || a.student_id || '';
    const idB = b.id || b.student_id || '';
    return idA.localeCompare(idB);
  });

  let currentY = MARGIN;
  let pageNumber = 1;
  let studentIndex = 0;

  // Şagirdləri sıra ilə işlə
  for (let i = 0; i < sortedStudents.length; i++) {
    const student = sortedStudents[i];
    const studentIndexInList = i + 1;

    // Progress callback
    if (onProgress) {
      onProgress(i + 1, sortedStudents.length);
    }

    // Şagirdin sual məlumatını al
    const questionData = (student as any)[questionNumber] as QuestionData;
    const imageUrl = typeof questionData === 'string' ? questionData : questionData?.path;

    if (!imageUrl) {
      console.warn(`⚠️ Şagird ${student.student_id} üçün ${questionNumber} sualı üçün şəkil tapılmadı`);
      continue;
    }

    // Şəkli yüklə - Firebase Storage SDK istifadə et (CORS problemi üçün)
    let img: HTMLImageElement;
    let imageDataUrl: string;
    try {
      // Firebase Storage URL-i olub-olmadığını yoxla
      if (imageUrl.includes('firebasestorage.googleapis.com')) {
        const path = extractPathFromFirebaseUrl(imageUrl);
        if (path) {
          // Firebase Storage SDK istifadə et
          const storage = getStorage(app);
          const storageRef = ref(storage, path);
          const downloadURL = await getDownloadURL(storageRef);
          
          // Download URL-dən Image yüklə
          img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = downloadURL;
          });
          
          // Canvas-a çevir (CORS problemi üçün)
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
          } else {
            throw new Error('Canvas context alına bilmədi');
          }
        } else {
          throw new Error('Firebase Storage path çıxarıla bilmədi');
        }
      } else {
        // Digər URL-lər üçün normal yüklə
        img = await loadImage(imageUrl);
        // Canvas-a çevir
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
        } else {
          throw new Error('Canvas context alına bilmədi');
        }
      }
    } catch (error) {
      console.error(`❌ Şəkil yüklənə bilmədi: ${imageUrl}`, error);
      continue;
    }

    // Header məlumatları
    const headerText = [
      `${studentIndexInList}. ${maskName(student.firstName, student.lastName)} - ${assignment.fenn} Qiymətləndirməsi - ${questionNumberDisplay}. sual`,
      `İştirakçı No: ${maskStudentId(student.student_id)}`,
      `Sinif: ${student.grade}`,
      `Variant: ${student.variant || '-'}`,
      `Sıra: ${studentIndexInList} / ${sortedStudents.length}`
    ];

    // Header yüksəkliyini hesabla
    const headerLineHeight = 24;
    const totalHeaderHeight = headerText.length * headerLineHeight + 20; // 20px padding

    // Şəkil ölçülərini hesabla
    const maxImageWidth = CONTENT_WIDTH;
    const maxImageHeight = MAX_CONTENT_HEIGHT - totalHeaderHeight;
    const { width: imageWidth, height: imageHeight } = calculateImageDimensions(
      img,
      maxImageWidth,
      maxImageHeight
    );

    // Cəmi yüksəklik: header + şəkil + boşluq
    const totalItemHeight = totalHeaderHeight + imageHeight + 20; // 20px boşluq

    // Yeni səhifə lazımdırsa
    if (currentY + totalItemHeight > A4_HEIGHT - MARGIN) {
      pdf.addPage();
      pageNumber++;
      currentY = MARGIN;
    }

    // Header yaz
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 41, 59); // #1e293b

    headerText.forEach((line, index) => {
      pdf.text(line, MARGIN, currentY + (index * headerLineHeight) + 16);
    });

    currentY += totalHeaderHeight;

    // Şəkli PDF-ə əlavə et (artıq base64 data URL hazırdır)
    try {
      pdf.addImage(
        imageDataUrl,
        'JPEG',
        MARGIN,
        currentY,
        imageWidth,
        imageHeight
      );
      currentY += imageHeight + 20; // 20px boşluq növbəti şagird üçün
    } catch (error) {
      console.error(`❌ Şəkil PDF-ə əlavə edilə bilmədi:`, error);
      continue;
    }

    studentIndex++;
  }

  // PDF faylını yüklə
  const fileName = `${assignment.fenn}_Sinif${assignment.sinif}_Variant${assignment.variant || assignment.qrup}_Sual${questionNumberDisplay}.pdf`;
  pdf.save(fileName);

  console.log(`✅ PDF export tamamlandı: ${fileName} (${studentIndex} şagird, ${pageNumber} səhifə)`);
  
  // Progress callback - tamamlandı
  if (onProgress) {
    onProgress(sortedStudents.length, sortedStudents.length);
  }
};

// PDF export funksiyası - artıq yüklənmiş şəkilləri istifadə et (modal yanaşması)
export const exportBuraxilisQuestionPDFFromImages = async (
  assignment: YoklaAssignment,
  questionNumber: string,
  students: ScannedData[],
  loadedImages: Map<string, HTMLImageElement>
): Promise<void> => {
  // jsPDF dinamik import
  const { default: jsPDF } = await import('jspdf');

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [A4_WIDTH, A4_HEIGHT]
  });

  const questionNumberDisplay = questionNumber.replace(/^Q/i, '');
  
  // Şagirdləri document ID-yə görə sırala
  const sortedStudents = [...students].sort((a, b) => {
    const idA = a.id || a.student_id || '';
    const idB = b.id || b.student_id || '';
    return idA.localeCompare(idB);
  });

  let currentY = MARGIN;
  let pageNumber = 1;
  let studentIndex = 0;

  // Şagirdləri sıra ilə işlə
  for (let i = 0; i < sortedStudents.length; i++) {
    const student = sortedStudents[i];
    const studentIndexInList = i + 1;
    const imageKey = student.id || student.student_id;
    const img = loadedImages.get(imageKey);

    if (!img) {
      console.warn(`⚠️ Şagird ${student.student_id} üçün şəkil tapılmadı`);
      continue;
    }

    // Header məlumatları
    const headerText = [
      `${studentIndexInList}. ${maskName(student.firstName, student.lastName)} - ${assignment.fenn} Qiymətləndirməsi - ${questionNumberDisplay}. sual`,
      `İştirakçı No: ${maskStudentId(student.student_id)}`,
      `Sinif: ${student.grade}`,
      `Variant: ${student.variant || '-'}`,
      `Sıra: ${studentIndexInList} / ${sortedStudents.length}`
    ];

    // Header yüksəkliyini hesabla
    const headerLineHeight = 24;
    const totalHeaderHeight = headerText.length * headerLineHeight + 20;

    // Şəkil ölçülərini hesabla
    const maxImageWidth = CONTENT_WIDTH;
    const maxImageHeight = MAX_CONTENT_HEIGHT - totalHeaderHeight;
    const aspectRatio = img.width / img.height;
    let imageWidth = maxImageWidth;
    let imageHeight = imageWidth / aspectRatio;

    if (imageHeight > maxImageHeight) {
      imageHeight = maxImageHeight;
      imageWidth = imageHeight * aspectRatio;
    }

    // Cəmi yüksəklik: header + şəkil + boşluq
    const totalItemHeight = totalHeaderHeight + imageHeight + 20;

    // Yeni səhifə lazımdırsa
    if (currentY + totalItemHeight > A4_HEIGHT - MARGIN) {
      pdf.addPage();
      pageNumber++;
      currentY = MARGIN;
    }

    // Header yaz
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 41, 59);

    headerText.forEach((line, index) => {
      pdf.text(line, MARGIN, currentY + (index * headerLineHeight) + 16);
    });

    currentY += totalHeaderHeight;

    // Canvas-a çevir və PDF-ə əlavə et
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Canvas context alına bilmədi');
      }
      
      ctx.drawImage(img, 0, 0);
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      
      pdf.addImage(
        imageDataUrl,
        'JPEG',
        MARGIN,
        currentY,
        imageWidth,
        imageHeight
      );
      currentY += imageHeight + 20;
    } catch (error) {
      console.error(`❌ Şəkil PDF-ə əlavə edilə bilmədi:`, error);
      continue;
    }

    studentIndex++;
  }

  // PDF faylını yüklə
  const fileName = `${assignment.fenn}_Sinif${assignment.sinif}_Variant${assignment.variant || assignment.qrup}_Sual${questionNumberDisplay}.pdf`;
  pdf.save(fileName);

  console.log(`✅ PDF export tamamlandı: ${fileName} (${studentIndex} şagird, ${pageNumber} səhifə)`);
};

